/** @format */

const IN_DELIMITER = '~';

const ConditionTypes = {
  Or: logicStatements => logicStatements.reduce((acc, next) => acc || next),
  And: logicStatements => logicStatements.reduce((acc, next) => acc && next),
  Single: logicStatements => logicStatements[0] === true,
};

const OperatorTypes = {
  Equal: (a, b) => a === b,
  NotEqual: (a, b) => a !== b,
  Greater: (a, b) => a > b,
  Less: (a, b) => a < b,
  In: (needle, haystack) => haystack.split(IN_DELIMITER).includes(needle),
  NotIn: (needle, haystack) => needle !== undefined && haystack.split(IN_DELIMITER).includes(needle) === false,
};

function translateLogicStatement(data) {
  const result = {
    page: data.dataPage,
    id: data.dataRequest,
    operator: data.operator,
    isSamePage: data.isSamePage,
    otherPageValue: data.otherPageValue,
    trigger: data.triggeringValue,
  };
  return result;
}

function transformTriggerCompareValue(element, value) {
  const curControlTypeName = element && element.controlTypeName;
  const origControlTypeName = element && element.origControlTypeName;
  let transformedValue = value;

  // support `ReadOnly` in HTCombo
  const controlTypeName = origControlTypeName || curControlTypeName;

  switch (controlTypeName) {
    case 'Date':
      if (value !== null) {
        let dateVal = value ? new Date(value) : value;
        if (dateVal && dateVal !== 'Invalid Date') {
          transformedValue = dateVal.getTime();
        }
      }
      break;
    case 'Number':
      transformedValue = parseInt(value, 10) || undefined;
      break;
    default:
      break;
  }
  return transformedValue;
}

function getTriggerValue(allElements, allElementsIndex, data) {
  const position = allElementsIndex[data.id] && allElementsIndex[data.id][0];

  if (position === undefined || !data.isSamePage || (position !== undefined && !allElements[position].showLogicLookUp)) {
    // if position is undefined there is no transformation to be done
    if (position === undefined) {
      return data.trigger;
    }

    return transformTriggerCompareValue(allElements[position], data.trigger);
  }

  /*
   * The BE team has stated that for lookUp values it can NOT use the `lookUpItemValue` when evaluating showLogic.
   * Instead, we must work around this issue and use the `displayText` as the evaluating value.
   * A little confusing, but apparently this can not be changed.
   */

  /*
   * In the case of an In/NotIn operator the values must be swapped out for their showLogic equivalents and
   * Returned in the VALUE~VALUE~VALUE FORMAT
   */

  const lookUpValue = allElements[position].showLogicLookUp;

  if (!lookUpValue) return '';

  if (data.operator.includes('In', 'NotIn')) {
    const triggerVals = data.trigger.split(IN_DELIMITER).map(val => lookUpValue[val]);
    return triggerVals.join(IN_DELIMITER);
  }

  return transformTriggerCompareValue(allElements[position], lookUpValue[data.trigger]);
}

function getLogicPosition(allElements, allElementsIndex, data, logicIndexPosition = 0, parentId) {
  let logicPos = 0;

  if (parentId) {
    const logicEl = allElementsIndex[data.id];
    if (logicEl) {
      const elIndex = logicIndexPosition >= logicEl.length ? 0 : logicIndexPosition;
      const compareEl = allElements[logicEl[elIndex]];

      if (compareEl.parentId === parentId) {
        logicPos = logicIndexPosition;
      }
    }
  }

  return logicPos;
}

function getCompareVisiblity(allElements, allElementsIndex, data, logicIndexPosition = 0, parentId) {
  // return a Boolean whether an element is visible or not
  const logicPos = getLogicPosition(allElements, allElementsIndex, data, logicIndexPosition, parentId);
  const position = allElementsIndex[data.id] && allElementsIndex[data.id][logicPos];

  // if provided an element from another page just always return this as `true`
  // and let the showLogic statement evaluate whether it's visible or not
  if (position === undefined && !data.isSamePage) {
    return true;
  }

  let isCompareVisible = allElements[position].props.isComponentVisible;
  return isCompareVisible;
}

function computeLogic(data, elements, elementsIndex, logicIndexPosition, parentId) {
  const logicResult = traverseLogicCompute(data, elements, elementsIndex, logicIndexPosition, parentId);
  return logicResult;
}

function getCompareValue(allElements, allElementsIndex, data, logicIndexPosition = 0, parentId) {
  if (allElementsIndex[data.id] === undefined) {
    //console.warn(`A showLogic statement is referencing elementId '${data.id} that does not exist'`);
  }

  const logicPos = getLogicPosition(allElements, allElementsIndex, data, logicIndexPosition, parentId);
  const position = allElementsIndex[data.id] && allElementsIndex[data.id][logicPos];

  // exists on current page
  // doesnt exist on current page but backend provides value
  // should exist on current page but doesnt. return null

  if (data.isSamePage && position === undefined) {
    return undefined;
  } else if (position !== undefined && data.isSamePage) {
    const val = allElements[position].props.value;
    return transformTriggerCompareValue(allElements[position], val);
  } else if (!data.isSamePage) {
    return transformTriggerCompareValue(allElements[position], data.otherPageValue);
  } else {
    throw Error('Missing comparison value in showLogic/complexShowLogic statement.');
  }
}

function getConditionFn(operator) {
  return conditions => ConditionTypes[operator](conditions);
}

// if the id we are searching for does not exit
// then we just assume we're going to show it
function getLogicFn(data) {
  return (allElements, elementsIndex, logicIndexPosition, parentId) => {
    // this is a short term fix. the problem is the 'In' and 'NotIn' operators can not be applied
    // as it stands today to data objects where the `isSamePage` value is 'false'
    // can not be evaluated because the lookup table does not exist for the provided trigger value
    //
    // Two possible solutions have been raised:
    //   1) provide labels for triggerValues if `isSamePage` = 'false' so we can compare without lookup table
    //   or 2) provide lookup tables (more costly)
    //
    /** if (!data.isSamePage && data.operator.includes('In', 'NotIn')) {
      return true;
    }**/

    let compareVal = getCompareValue(allElements, elementsIndex, data, logicIndexPosition, parentId);

    if (compareVal === undefined) return false;
    if (compareVal === null) return null;

    if (!data.isSamePage && data.otherPageValue === '') return false;

    let triggerValue = getTriggerValue(allElements, elementsIndex, data);
    const isCompareVisible = getCompareVisiblity(allElements, elementsIndex, data, logicIndexPosition, parentId);

    const logicPos = getLogicPosition(allElements, elementsIndex, data, logicIndexPosition, parentId);
    const position = elementsIndex[data.id] && elementsIndex[data.id][logicPos];

    // this fix was added because we can not compare a date vs. and empty date
    // otherwise that date could always end up being greater or less than
    // resulting in a showLogic condition that returns true
    if (position !== undefined) {
      // when a control is provided no value (think initialization) then we should evaluate the showLogic
      // with a return value of `false` per business requirement the compareVal needs to exist
      if (compareVal === '') {
        if (allElements[position].controlTypeName === 'Date') {
          return false;
        }
        // compareVal empty and element is Dropdown then ...
        if (
          triggerValue &&
          triggerValue.includes('(Select)') &&
          (allElements[position].controlTypeName === 'DropDown' || allElements[position].controlTypeName === 'CountryList')
        ) {
          if (data.operator === 'Equal') {
            return true;
          }
          if (data.operator === 'NotEqual') {
            return false;
          }
          // We are going to consider "" as (Select).
          if (data.operator === 'NotIn' || data.operator === 'In') {
            triggerValue = transformTriggerValue(triggerValue);
          }
        }
      }
    }
    return OperatorTypes[data.operator](compareVal, triggerValue) && isCompareVisible;
  };
}

function transformTriggerValue(existingtriggerValue) {
  let updatedTrigger = existingtriggerValue.split('~');
  updatedTrigger.push('');
  return updatedTrigger.join('~');
}
function traverseLogicCompute(data, elements, elementsIndex, logicIndexPosition, parentId) {
  if (!Array.isArray(data) && !data.condition && !data.group) {
    const translatedLogicStatement = translateLogicStatement(data);
    return getLogicFn(translatedLogicStatement)(elements, elementsIndex, logicIndexPosition, parentId);
  }

  if (!data.group && data.condition && Array.isArray(data.condition) && data.condition.length) {
    return getConditionFn(data.operator)(
      data.condition.map(item => traverseLogicCompute(item, elements, elementsIndex, logicIndexPosition, parentId)),
    );
  }

  if (data.group && Array.isArray(data.group) && data.group.length) {
    if (data.condition) {
      return getConditionFn(data.operator)(
        data.condition
          .map(item => traverseLogicCompute(item, elements, elementsIndex, logicIndexPosition, parentId))
          .concat(...data.group.map(item => traverseLogicCompute(item, elements, elementsIndex, logicIndexPosition, parentId))),
      );
    } else {
      return getConditionFn(data.operator)(
        data.group.map(item => traverseLogicCompute(item, elements, elementsIndex, logicIndexPosition, parentId)),
      );
    }
  }
}

export { traverseLogicCompute, computeLogic, getConditionFn, getLogicFn, translateLogicStatement };
